import { getAdminClient } from '@beatq/sdk-client';
import { mapErrorCode, readListFromEnvelope } from '@beatq/shared';
import { getStoredAccessReason } from './adminSession.js';
import {
  auditSearchPath,
  caseDetailPath,
  customer360Path,
  merchant360Path,
  orderInvestigatePath,
  paymentTracePath,
} from './subjectUrls.js';

type ApiErr = { error?: { code: string; message: string } };

export type AdminSearchHit = {
  id: string;
  name: string;
  type: 'order' | 'case' | 'customer' | 'business' | 'payment' | 'audit' | 'branch' | string;
  href?: string;
  orderId?: string;
  caseId?: string;
};

export type PaletteAction = {
  id: string;
  name: string;
  href: string;
  keywords: readonly string[];
  anyOf?: readonly string[];
};

export const PALETTE_FREQUENT_ACTIONS: readonly PaletteAction[] = [
  {
    id: 'cases',
    name: 'Open case queue',
    href: '/case-queue',
    keywords: ['case', 'queue', 'support'],
    anyOf: ['PERM-support.case.list', 'PERM-support.case.read'],
  },
  {
    id: 'macros',
    name: 'Macro management',
    href: '/macro-management',
    keywords: ['macro', 'template'],
    anyOf: ['PERM-support.macro.manage', 'PERM-support.macro.use'],
  },
  {
    id: 'moderation',
    name: 'Moderation queue',
    href: '/moderation-queue',
    keywords: ['moderation', 'review'],
    anyOf: ['PERM-moderation.queue.read'],
  },
  {
    id: 'fraud',
    name: 'Fraud queue',
    href: '/fraud-queue',
    keywords: ['fraud', 'risk'],
    anyOf: ['PERM-fraud.case.read'],
  },
  {
    id: 'tenants',
    name: 'Tenant list',
    href: '/tenant-list',
    keywords: ['tenant', 'business'],
    anyOf: ['PERM-tenant.business.list'],
  },
  {
    id: 'emergency',
    name: 'Emergency controls',
    href: '/emergency-controls',
    keywords: ['emergency', 'pause', 'kill switch', 'incident'],
  },
  {
    id: 'approvals',
    name: 'Approval inbox',
    href: '/approval-inbox',
    keywords: ['approval', 'four-eyes', 'inbox'],
    anyOf: ['PERM-refund.refund.approve', 'PERM-approval.request.decide'],
  },
  {
    id: 'notifications',
    name: 'Notification centre',
    href: '/notification-centre',
    keywords: ['notification', 'alert', 'inbox'],
    anyOf: ['PERM-notification.history.read'],
  },
  {
    id: 'audit',
    name: 'Audit search',
    href: '/audit-search',
    keywords: ['audit', 'log'],
    anyOf: ['PERM-audit.log.read', 'PERM-audit.log.search'],
  },
  {
    id: 'beatq-del',
    name: 'BeatQ Del rides',
    href: '/beatq-del',
    keywords: ['beatq del', 'delivery', 'ride', 'courier', 'dispatch'],
    anyOf: ['PERM-delivery.job.read'],
  },
  {
    id: 'dashboard',
    name: 'Dashboard',
    href: '/admin-dashboard',
    keywords: ['dashboard', 'home'],
  },
];

const TYPE_PERMS: Record<string, readonly string[]> = {
  case: ['PERM-support.case.read', 'PERM-support.case.list', 'PERM-support.case.assign'],
  order: ['PERM-order.history.read', 'PERM-order.order.read', 'PERM-order.order.list'],
  customer: ['PERM-customer.profile.read'],
  business: ['PERM-tenant.business.read', 'PERM-tenant.business.list'],
  branch: ['PERM-tenant.business.read', 'PERM-branch.branch.list'],
  payment: ['PERM-payment.transaction.read'],
  audit: ['PERM-audit.log.read', 'PERM-audit.log.search'],
};

export function sdkErrorMessage(error: unknown): string {
  const e = error as ApiErr;
  return e?.error?.message ?? mapErrorCode(e?.error?.code ?? 'ERR-UNKNOWN');
}

export function inferHitType(q: string, hitType?: string): string {
  const declared = hitType?.toLowerCase();
  if (
    declared &&
    ['order', 'case', 'customer', 'business', 'payment', 'audit', 'branch'].includes(declared)
  ) {
    return declared;
  }
  const trimmed = q.trim();
  if (/^BQ[-_]/i.test(trimmed) || /^ORD[-_]/i.test(trimmed)) return 'order';
  if (/^SUP[-_]/i.test(trimmed) || /^CASE[-_]/i.test(trimmed)) return 'case';
  if (/^AUD[-_]/i.test(trimmed) || /^AE[-_]/i.test(trimmed)) return 'audit';
  if (/^PAY[-_]/i.test(trimmed) || /^pi_/i.test(trimmed)) return 'payment';
  if (/@/.test(trimmed)) return 'customer';
  if (/^\+?\d[\d\s()-]{7,}$/.test(trimmed)) return 'customer';
  if (/^[A-Z]{1,2}\d{1,2}[A-Z]?\s*\d[A-Z]{2}$/i.test(trimmed)) return 'business';
  return declared || 'subject';
}

export async function searchAdmin(q: string, limit = 8): Promise<AdminSearchHit[]> {
  const client = getAdminClient();
  const reason = getStoredAccessReason();
  const { data, error, response } = await client.GET('/admin/v1/search', {
    params: { query: { q, limit } as never },
    headers: reason ? ({ 'X-Access-Reason': reason } as never) : undefined,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  const rows = readListFromEnvelope<AdminSearchHit>(data, ['results', 'items']);
  return rows.map((hit) => ({
    ...hit,
    type: inferHitType(q, hit.type),
    id: hit.id,
    name: hit.name || hit.id,
  }));
}

export function hrefForSearchHit(hit: AdminSearchHit, caseId?: string | null): string {
  if (hit.href) return hit.href;
  const extra = caseId ? { caseId } : undefined;
  switch (hit.type) {
    case 'case':
      return caseDetailPath(hit.id);
    case 'order':
      return orderInvestigatePath(hit.id, extra);
    case 'customer':
      return customer360Path(hit.id, extra);
    case 'business':
    case 'branch':
      return merchant360Path(hit.id, extra);
    case 'payment':
      return hit.orderId
        ? paymentTracePath(hit.orderId, extra)
        : `/payment-trace?paymentId=${encodeURIComponent(hit.id)}`;
    case 'audit':
      return auditSearchPath(hit.id);
    default:
      return '/admin-dashboard';
  }
}

export function groupSearchHits(
  hits: AdminSearchHit[],
  q?: string,
): { type: string; items: AdminSearchHit[] }[] {
  const likely = q?.trim() ? inferHitType(q) : null;
  const order = ['case', 'order', 'customer', 'business', 'branch', 'payment', 'audit'];
  const map = new Map<string, AdminSearchHit[]>();
  for (const hit of hits) {
    const list = map.get(hit.type) ?? [];
    list.push(hit);
    map.set(hit.type, list);
  }
  const groups = [...map.entries()].map(([type, items]) => ({ type, items }));
  groups.sort((a, b) => {
    if (likely && a.type === likely && b.type !== likely) return -1;
    if (likely && b.type === likely && a.type !== likely) return 1;
    const ai = order.indexOf(a.type);
    const bi = order.indexOf(b.type);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });
  return groups;
}

export function filterAuthorisedHits(
  hits: AdminSearchHit[],
  can: (perm: string) => boolean,
  permissionsReady: boolean,
): AdminSearchHit[] {
  if (!permissionsReady) return hits;
  return hits.filter((hit) => {
    const perms = TYPE_PERMS[hit.type];
    if (!perms?.length) return true;
    return perms.some((p) => can(p));
  });
}

export function matchFrequentActions(
  q: string,
  can: (perm: string) => boolean,
  permissionsReady: boolean,
): PaletteAction[] {
  const needle = q.trim().toLowerCase();
  if (needle.length < 1) return [];
  return PALETTE_FREQUENT_ACTIONS.filter((action) => {
    if (permissionsReady && action.anyOf?.length && !action.anyOf.some((p) => can(p))) {
      return false;
    }
    const hay = `${action.name} ${action.keywords.join(' ')}`.toLowerCase();
    return hay.includes(needle) || needle.split(/\s+/).every((part) => hay.includes(part));
  });
}

export function subjectNeedsPurposeBinding(
  hit: AdminSearchHit,
  openCaseId: string | null,
): boolean {
  if (openCaseId) return false;
  if (hit.type === 'case') return false;
  return ['order', 'customer', 'business', 'branch', 'payment', 'audit'].includes(hit.type);
}

export const PURPOSE_JUSTIFICATION_MIN = 12;
